import React, { createContext, useContext, useState, useEffect } from "react";
import { User } from "../types/user";
import {
  loginUser,
  registerUser,
  logoutUser,
  forgotPassword,
  resetPassword,
  verifyResetCode as verifyCode,
  getCurrentUser,
} from "../services/auth";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
  verifyResetCode: (code: string) => Promise<boolean>;
  updateUser: (updatedUser: User) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  requestPasswordReset: async () => {},
  confirmPasswordReset: async () => {},
  verifyResetCode: async () => false,
  updateUser: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing Cognito session on app load
    const checkAuth = async () => {
      try {
        // Try to get current authenticated user from Cognito
        const userData = await getCurrentUser();
        if (userData) {
          setUser(userData);
          // Update localStorage with current token
          localStorage.setItem("authToken", userData.token || "");
        } else {
          // No valid session, remove any stale tokens
          localStorage.removeItem("authToken");
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        // Clear any stale tokens on auth failure
        localStorage.removeItem("authToken");
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const userData = await loginUser(email, password);
      setUser(userData);
      localStorage.setItem("authToken", userData.token || "");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Login failed";
      setError(errorMessage);
      console.error("Login error:", err);
      throw new Error(errorMessage); // Re-throw to allow UI to handle specific error
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any) => {
    try {
      setError(null);
      setLoading(true);
      await registerUser(userData);
      // Don't set user as logged in - they need to confirm email first
      // The UI should redirect to confirmation page
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Registration failed";
      setError(errorMessage);
      console.error("Registration error:", err);
      throw new Error(errorMessage); // Re-throw to allow UI to handle specific error
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      setLoading(true);
      await logoutUser();
      setUser(null);
      localStorage.removeItem("authToken");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Logout failed";
      setError(errorMessage);
      console.error("Logout error:", err);
      throw new Error(errorMessage); // Re-throw to allow UI to handle specific error
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      await forgotPassword(email);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Password reset request failed";
      setError(errorMessage);
      console.error("Password reset request error:", err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmPasswordReset = async (code: string, newPassword: string) => {
    try {
      setError(null);
      setLoading(true);
      await resetPassword(code, newPassword);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Password reset failed";
      setError(errorMessage);
      console.error("Password reset error:", err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyResetCode = async (code: string): Promise<boolean> => {
    try {
      setLoading(true);
      return await verifyCode(code);
    } catch (err) {
      console.error("Reset code verification error:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        requestPasswordReset,
        confirmPasswordReset,
        verifyResetCode,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
